// CareLogix™ — Gate 1: Privacy & Safety Foundation (Phase 32.2)
// First constitutional gate. Establishes CareLogix’s privacy/safety/trust posture
// before ORACLE-specific risk disclosure (Gate 2) and Membership selection (Gate 3).
// Storage key: carelogix_privacy_safety_ack_v1
import React from "react";
import { motion } from "framer-motion";
import { PALETTE } from "@/components/animation/MotionShared";

export const CARELOGIX_PRIVACY_KEY = "carelogix_privacy_safety_ack_v1";

export function isPrivacyAcknowledged() {
  if (typeof window === "undefined") return true;
  try { return window.localStorage.getItem(CARELOGIX_PRIVACY_KEY) === "true"; }
  catch { return false; }
}

export function setPrivacyAcknowledged(v) {
  if (typeof window === "undefined") return;
  try {
    if (v) window.localStorage.setItem(CARELOGIX_PRIVACY_KEY, "true");
    else   window.localStorage.removeItem(CARELOGIX_PRIVACY_KEY);
  } catch (_) { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent("carelogix:privacy-ack-changed", { detail: { value: !!v } })); } catch (_) { /* ignore */ }
}

const BADGES = [
  { label: "PRIVACY-FIRST",       color: PALETTE.emerald },
  { label: "SAFETY GUARDED",      color: PALETTE.greenAccent },
  { label: "HUMAN-IN-THE-LOOP",   color: PALETTE.amber },
  { label: "ETHICAL AI",          color: PALETTE.gold },
];

const PROTECTS = [
  "Your identity — minimum data collected, never sold.",
  "Your decisions — ORACLE assists; you decide.",
  "Your boundaries — you can revoke consent at any time.",
  "Your dignity — outputs are explainable, not opaque.",
  "Your jurisdiction — region-aware compliance always.",
];

const SAFETY_RULES = [
  "Human-in-the-loop required for every consequential decision.",
  "No autonomous trading. No autonomous wagering. No autonomous medical actions.",
  "Healthcare information processed from public sources only — MNPI is prohibited.",
  "Sports content is 21+ where applicable and jurisdiction-gated.",
  "Confidence, uncertainty, assumptions, and risk are always surfaced.",
];

const CONSENT = [
  "I understand CareLogix is research, intelligence, and decision-support — not advice.",
  "I will use ORACLE outputs to inform, not replace, my own judgment.",
  "I accept the privacy and safety principles described above.",
];

export default function CareLogixPrivacyGate({ onAccept, testid = "carelogix-privacy-gate" }) {
  const handleAccept = () => {
    setPrivacyAcknowledged(true);
    if (typeof onAccept === "function") {
      try { onAccept(); } catch (_) { /* ignore */ }
    }
  };

  return (
    <main style={S.shell} data-testid={testid} role="dialog" aria-modal="true" aria-labelledby={`${testid}-title`}>
      <div style={S.bgGlow} aria-hidden="true" />
      <motion.section
        style={S.card}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <span style={S.gateChip} data-testid={`${testid}-step`}>GATE 1 OF 3</span>

        <header style={S.header}>
          <span style={S.eyebrow}>CARELOGIX™ CONTINUITY™</span>
          <h1 style={S.title} id={`${testid}-title`} data-testid={`${testid}-title`}>Privacy &amp; Safety Foundation</h1>
          <p style={S.subtitle} data-testid={`${testid}-subtitle`}>
            Your trust is the foundation of every CareLogix experience. Please review and accept the
            principles below to continue.
          </p>
        </header>

        <div style={S.badgeRow} data-testid={`${testid}-badges`}>
          {BADGES.map((b) => (
            <span
              key={b.label}
              style={{ ...S.badge, color: b.color, borderColor: b.color, boxShadow: `0 0 16px ${b.color}33 inset` }}
              data-testid={`${testid}-badge-${b.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            >
              <span style={{ ...S.badgeDot, background: b.color }} aria-hidden="true" />
              {b.label}
            </span>
          ))}
        </div>

        <p style={S.lead}>
          CareLogix is a <strong style={{ color: PALETTE.goldBright }}>privacy-first intelligence platform</strong>.
          We design every system, agent, and dashboard to keep you in control.
        </p>

        <div style={S.twoCol}>
          <div style={S.column} data-testid={`${testid}-protect-block`}>
            <h3 style={S.colTitle}>WHAT CARELOGIX PROTECTS</h3>
            <ul style={S.list}>
              {PROTECTS.map((line) => (
                <li style={S.listItem} key={line}>
                  <span style={{ ...S.bullet, color: PALETTE.emerald }} aria-hidden="true">✓</span>
                  {line}
                </li>
              ))}
            </ul>
          </div>
          <div style={S.column} data-testid={`${testid}-safety-block`}>
            <h3 style={S.colTitle}>SAFETY RULES IN EFFECT</h3>
            <ul style={S.list}>
              {SAFETY_RULES.map((line) => (
                <li style={S.listItem} key={line}>
                  <span style={{ ...S.bullet, color: PALETTE.amber }} aria-hidden="true">✱</span>
                  {line}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div style={S.consentBox} data-testid={`${testid}-consent-box`}>
          <h3 style={S.consentTitle}>BY CONTINUING, YOU ACKNOWLEDGE</h3>
          <ul style={S.consentList}>
            {CONSENT.map((line) => (
              <li style={S.consentItem} key={line}>
                <span style={{ ...S.bullet, color: PALETTE.gold }} aria-hidden="true">◈</span>
                {line}
              </li>
            ))}
          </ul>
        </div>

        <div style={S.ctaRow}>
          <button
            type="button"
            onClick={handleAccept}
            style={S.ctaPrimary}
            data-testid={`${testid}-continue`}
            aria-label="Accept CareLogix privacy and safety principles and continue"
          >
            <span style={{ marginRight: 10 }} aria-hidden="true">🛡️</span>
            ACCEPT &amp; CONTINUE
            <span style={{ marginLeft: 10 }} aria-hidden="true">→</span>
          </button>
        </div>

        <footer style={S.footer}>
          <span style={S.footerLink}>Privacy Policy</span>
          <span style={S.footerSep}>|</span>
          <span style={S.footerLink}>Terms of Service</span>
          <span style={S.footerSep}>|</span>
          <span style={S.footerLink}>Accessibility</span>
        </footer>

        <div style={S.guard}>
          <span aria-hidden="true">🔐</span>
          <span>CareLogix™ Trust Infrastructure · Human dignity preserved by design.</span>
        </div>
      </motion.section>
    </main>
  );
}

const S = {
  shell:    { position: "fixed", inset: 0, zIndex: 9990, background: "radial-gradient(circle at 50% 0%, rgba(15,45,30,0.55) 0%, rgba(4,6,8,1) 65%)", overflowY: "auto", padding: "24px 16px 48px", display: "flex", alignItems: "flex-start", justifyContent: "center", fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  bgGlow:   { position: "absolute", inset: 0, backgroundImage: "radial-gradient(ellipse 600px 320px at 50% 6%, rgba(34,197,94,0.10), transparent 65%)", pointerEvents: "none" },
  card:     { position: "relative", width: "100%", maxWidth: 860, background: "linear-gradient(180deg, rgba(11,15,20,0.96), rgba(6,18,12,0.96))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 24, padding: "28px clamp(20px, 4vw, 44px) 24px", color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 14, boxShadow: "0 30px 80px rgba(0,0,0,0.55), inset 0 0 60px rgba(34,197,94,0.05)" },
  gateChip: { alignSelf: "center", color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.32em", padding: "4px 10px", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 999, background: "rgba(197,160,89,0.06)" },
  header:   { display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 6 },
  eyebrow:  { color: PALETTE.greenAccent, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.32em" },
  title:    { margin: 0, color: PALETTE.goldBright, fontSize: "clamp(22px, 4vw, 34px)", letterSpacing: "0.06em", fontWeight: 800 },
  subtitle: { margin: 0, color: PALETTE.slate3, fontSize: "clamp(13px, 1.6vw, 15px)", maxWidth: 620, lineHeight: 1.55 },
  badgeRow: { display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8 },
  badge:    { display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(11,15,20,0.7)", border: "1px solid", padding: "6px 12px", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.14em" },
  badgeDot: { width: 7, height: 7, borderRadius: "50%", display: "inline-block" },
  lead:     { margin: 0, textAlign: "center", lineHeight: 1.6, fontSize: 14.5, color: PALETTE.slate3 },
  twoCol:   { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 },
  column:   { background: "rgba(4,6,8,0.6)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: 14, display: "flex", flexDirection: "column", gap: 8 },
  colTitle: { margin: 0, color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 11.5, letterSpacing: "0.14em" },
  list:     { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 },
  listItem: { display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13.5, lineHeight: 1.45, color: PALETTE.slate3 },
  bullet:   { fontFamily: "'Space Mono', monospace", fontSize: 13, width: 14, textAlign: "center" },
  consentBox:   { background: "rgba(197,160,89,0.05)", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 14, padding: 14, display: "flex", flexDirection: "column", gap: 8 },
  consentTitle: { margin: 0, color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 11.5, letterSpacing: "0.18em", textAlign: "center" },
  consentList:  { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 },
  consentItem:  { display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13.5, lineHeight: 1.45, color: PALETTE.slate3 },
  ctaRow:   { display: "flex", flexDirection: "column", gap: 8, marginTop: 4 },
  ctaPrimary:{ appearance: "none", width: "100%", background: "linear-gradient(180deg, #34d399 0%, #10b981 100%)", color: "#0b0f14", border: `1px solid ${PALETTE.emerald}`, boxShadow: "0 12px 30px rgba(16,185,129,0.30), inset 0 -2px 0 rgba(0,0,0,0.15)", padding: "14px 18px", borderRadius: 14, fontWeight: 800, fontFamily: "'Space Mono', monospace", letterSpacing: "0.14em", fontSize: 14, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" },
  footer:   { marginTop: 6, display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 10, color: PALETTE.slate4, fontSize: 11.5 },
  footerLink:{ textDecoration: "underline dotted", cursor: "pointer", color: PALETTE.gold },
  footerSep:{ color: "rgba(255,255,255,0.18)" },
  guard:    { display: "flex", justifyContent: "center", alignItems: "center", gap: 8, color: PALETTE.slate5, fontSize: 10.5, textAlign: "center" },
};
